import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://91miaozan.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `http://91miaozan.com/category/${slug}`,
      },
    ],
  };

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 爆瓜网`,
    description: category.description,
    url: `http://91miaozan.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: a.title,
        url: `http://91miaozan.com/article/${a.slug}`,
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionPageSchema) }} />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          <div className="mt-6 mb-10">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              {category.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">{category.description}</p>
          </div>

          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-400 text-sm">暂无相关文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden cursor-pointer hover:border-background-300 transition-colors duration-200"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-foreground-950 font-semibold text-sm md:text-base leading-snug line-clamp-2 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h2>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="whitespace-nowrap">{article.author}</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{article.date}</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{article.views.toLocaleString()} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}